import React, { useCallback, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { Form } from 'react-bootstrap'
import { useField, useFormikContext, getIn } from 'formik'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import moment from 'moment-timezone'

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const POPOVER_WIDTH = 292
// Used only before the portaled calendar has a measured height.
const POPOVER_EST_HEIGHT = 292

const parseStoredDate = (value) => {
  if (!value) return null
  const mmddyyyy = moment(value, 'MM/DD/YYYY', true)
  if (mmddyyyy.isValid()) return mmddyyyy.startOf('day')
  const iso = moment(value, 'YYYY-MM-DD', true)
  if (iso.isValid()) return iso.startOf('day')
  return null
}

const toMomentBoundary = (value) => {
  if (!value) return null
  if (moment.isMoment(value)) return value.clone().startOf('day')
  return parseStoredDate(value)
}

const formatDisplayDate = (value) => {
  const parsed = parseStoredDate(value)
  return parsed ? parsed.format('D MMMM YYYY') : null
}

const formatFormValue = (date) => date.format('MM/DD/YYYY')

const ExperienceDatePicker = ({
  formGroupProps: {
    className: formGroupClassName,
    ...otherFormGroupProps
  } = {},
  label,
  labelProps,
  className,
  spanClassName = '',
  showErrorMessage,
  customErrorMessage,
  placeholder = 'Add date',
  onBlur,
  onValueChange,
  minimumDate = null,
  maximumDate = null,
  disabled,
  name,
  displayFlex = false,
  isInvalid: isInvalidProp = false,
  errorMessage,
  rangeStart,
  rangeEnd,
  opens: _opens,
  position = 'down',
  ...rest
}) => {
  const triggerRef = useRef(null)
  const popoverRef = useRef(null)
  const [isOpen, setIsOpen] = useState(false)
  const [viewMonth, setViewMonth] = useState(() => moment().startOf('month'))
  const [popoverStyle, setPopoverStyle] = useState({ top: 0, left: 0 })

  const { setFieldValue, values } = useFormikContext()
  const [field, meta] = useField({
    ...rest,
    ...{ type: 'text' },
    name,
  })
  const { value: _ignoredValue, onChange: _ignoredChange, ...fieldRest } = field
  const fieldValue = getIn(values, name)

  const minMoment = toMomentBoundary(minimumDate)
  const maxMoment = toMomentBoundary(maximumDate)
  const rangeStartMoment = toMomentBoundary(rangeStart)
  const rangeEndMoment = toMomentBoundary(rangeEnd)
  const selectedMoment = parseStoredDate(fieldValue)

  const showInvalid =
    isInvalidProp ||
    (meta.touched && !!meta.error) ||
    (showErrorMessage && !!(errorMessage || meta.error))

  const displayText = formatDisplayDate(fieldValue) || placeholder
  const hasValue = Boolean(formatDisplayDate(fieldValue))

  const updatePopoverPosition = useCallback(() => {
    const trigger = triggerRef.current
    if (!trigger) return

    const rect = trigger.getBoundingClientRect()
    const measuredHeight = popoverRef.current
      ? popoverRef.current.getBoundingClientRect().height
      : 0
    const popoverHeight = measuredHeight || POPOVER_EST_HEIGHT
    const gap = 8

    let left = Math.min(
      Math.max(12, rect.left),
      window.innerWidth - POPOVER_WIDTH - 12,
    )
    let top = rect.bottom + gap

    const spaceBelow = window.innerHeight - rect.bottom - gap
    const spaceAbove = rect.top - 12

    // In short modal viewports, prefer opening above — but always use the
    // real (or tight estimated) height so the calendar sits next to the field.
    if (position === 'up') {
      top = rect.top - gap - popoverHeight
    } else if (
      position === 'auto' &&
      spaceBelow < popoverHeight &&
      spaceAbove > spaceBelow
    ) {
      top = rect.top - gap - popoverHeight
    }

    top = Math.min(top, window.innerHeight - popoverHeight - 12)
    top = Math.max(12, top)

    setPopoverStyle({ top, left })
  }, [position])

  const closePopover = useCallback(() => {
    setIsOpen(false)
  }, [])

  const openPopover = useCallback(() => {
    if (disabled) return
    // Open on the selected or range month, otherwise on today; minimumDate only limits selection
    const initialMonth =
      selectedMoment || rangeStartMoment || moment().startOf('day')
    setViewMonth(initialMonth.clone().startOf('month'))
    setIsOpen(true)
  }, [disabled, rangeStartMoment, selectedMoment])

  const handleSelectDate = useCallback(
    (day) => {
      const nextValue = formatFormValue(day)
      const setValueResult = setFieldValue(name, nextValue)
      if (onValueChange) {
        onValueChange(nextValue, setValueResult)
      }
      closePopover()
      if (onBlur) {
        onBlur()
      }
    },
    [closePopover, name, onBlur, onValueChange, setFieldValue],
  )

  useEffect(() => {
    if (!isOpen) return undefined

    updatePopoverPosition()
    // Second pass after paint so we can use the real calendar height (avoids
    // a large gap when flipping above the field inside short modals).
    const rafId = requestAnimationFrame(() => updatePopoverPosition())

    const handleResize = () => updatePopoverPosition()
    const handleScroll = () => updatePopoverPosition()

    window.addEventListener('resize', handleResize)
    window.addEventListener('scroll', handleScroll, true)

    return () => {
      cancelAnimationFrame(rafId)
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [isOpen, updatePopoverPosition, viewMonth])

  useEffect(() => {
    if (!isOpen) return undefined

    const handlePointerDown = (event) => {
      const target = event.target
      if (
        triggerRef.current?.contains(target) ||
        popoverRef.current?.contains(target)
      ) {
        return
      }
      closePopover()
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closePopover()
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [closePopover, isOpen])

  const renderLabel = () => {
    if (!label) return null
    return (
      <Form.Label {...labelProps} htmlFor={field.name}>
        {label}
      </Form.Label>
    )
  }

  const isDayDisabled = (day) => {
    if (minMoment && day.isBefore(minMoment, 'day')) return true
    if (maxMoment && day.isAfter(maxMoment, 'day')) return true
    return false
  }

  const isDayInRange = (day) => {
    if (!rangeStartMoment || !rangeEndMoment) return false
    if (rangeStartMoment.isAfter(rangeEndMoment, 'day')) return false
    const isSelected = selectedMoment && day.isSame(selectedMoment, 'day')
    if (isSelected) return false
    return (
      day.isSameOrAfter(rangeStartMoment, 'day') &&
      day.isSameOrBefore(rangeEndMoment, 'day')
    )
  }

  const renderCalendar = () => {
    const monthStart = viewMonth.clone().startOf('month')
    const gridStart = monthStart.clone().startOf('week')
    const today = moment().startOf('day')
    const days = []

    for (let i = 0; i < 42; i += 1) {
      const day = gridStart.clone().add(i, 'days')
      const isAdjacentMonth = !day.isSame(monthStart, 'month')
      const isSelected = selectedMoment && day.isSame(selectedMoment, 'day')
      const isToday = day.isSame(today, 'day')
      const dayDisabled = isDayDisabled(day)

      days.push(
        <button
          key={day.format('YYYY-MM-DD')}
          type='button'
          className={classNames({
            muted: isAdjacentMonth,
            selected: isSelected,
            'in-range': isDayInRange(day),
            today: isToday,
          })}
          disabled={dayDisabled}
          onClick={() => {
            if (!dayDisabled) {
              handleSelectDate(day)
            }
          }}>
          {day.date()}
        </button>,
      )
    }

    return (
      <div
        ref={popoverRef}
        className={classNames('experience-date-pop', { open: isOpen })}
        style={{
          top: popoverStyle.top,
          left: popoverStyle.left,
        }}
        role='dialog'
        aria-label='Choose date'>
        <div className='experience-date-pop-head'>
          <div className='experience-date-pop-title'>
            {viewMonth.format('MMMM YYYY')}
          </div>
          <div className='experience-date-pop-nav'>
            <button
              type='button'
              aria-label='Previous month'
              onClick={() =>
                setViewMonth((current) => current.clone().subtract(1, 'month'))
              }>
              ‹
            </button>
            <button
              type='button'
              aria-label='Next month'
              onClick={() =>
                setViewMonth((current) => current.clone().add(1, 'month'))
              }>
              ›
            </button>
          </div>
        </div>
        <div className='experience-date-week'>
          {WEEKDAYS.map((weekday) => (
            <span key={weekday}>{weekday}</span>
          ))}
        </div>
        <div className='experience-date-grid'>{days}</div>
      </div>
    )
  }

  return (
    <span
      className={classNames(
        'experience-date-picker-wrap',
        spanClassName,
        formGroupClassName,
      )}
      {...otherFormGroupProps}>
      <div
        className={classNames(
          { 'd-lg-flex': displayFlex },
          'align-items-center w-100',
        )}>
        {renderLabel()}
        <button
          {...fieldRest}
          ref={triggerRef}
          type='button'
          id={field.name}
          disabled={disabled}
          className={classNames(
            'experience-f-control experience-date-field',
            className,
            {
              ghost: !hasValue,
              'is-invalid': showInvalid,
            },
          )}
          onClick={(event) => {
            event.preventDefault()
            if (isOpen) {
              closePopover()
            } else {
              openPopover()
            }
          }}
          onBlur={(event) => {
            field.onBlur(event)
            if (onBlur && !isOpen) {
              onBlur(event)
            }
          }}>
          {displayText}
        </button>
        {showErrorMessage && (errorMessage || meta.error) ? (
          <Form.Text className='experience-f-error'>
            <span>{errorMessage || meta.error}</span>
          </Form.Text>
        ) : null}
        {customErrorMessage}
      </div>
      {isOpen && typeof document !== 'undefined'
        ? ReactDOM.createPortal(renderCalendar(), document.body)
        : null}
    </span>
  )
}

ExperienceDatePicker.defaultProps = {
  showErrorMessage: false,
  disabled: false,
  displayFlex: false,
  placeholder: 'Add date',
  position: 'down',
}

ExperienceDatePicker.propTypes = {
  showErrorMessage: PropTypes.bool,
  isInvalid: PropTypes.bool,
  onValueChange: PropTypes.func,
  onBlur: PropTypes.func,
  position: PropTypes.oneOf(['down', 'up', 'auto']),
  opens: PropTypes.oneOf(['left', 'right', 'center']),
  rangeStart: PropTypes.string,
  rangeEnd: PropTypes.string,
}

export default ExperienceDatePicker
